import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useDropzone } from 'react-dropzone';
import { useState } from 'react';
import parse from 'html-react-parser';
import { FileType, FileuploadSingleElementType } from '../helpers/types';
import { useTranslation } from 'react-i18next';

type Props = {
    onHandleDrop: (file: FileType & { preview: string }) => void;
    onRemoveFile: () => void;
    file: File;
    error: string | null;
    maxFileSizeMB?: number;
};

const FileUploadSingle = (props: FileuploadSingleElementType & Props) => {
    const { extensionsFormat, onHandleDrop, onRemoveFile, file, error, name, placeholder, displayLabel, label, description } = props;
    const { t } = useTranslation();

    const extensionToMime: { [key: string]: string } = {
        docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        doc: 'application/msword',
        xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        xlsm: 'application/vnd.ms-excel',
        csv: 'text/csv',
        xls: 'application/vnd.ms-excel',
        ppt: 'application/vnd.ms-powerpoint',
        pptx: 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
        pdf: 'application/pdf',
        jpg: 'image/jpeg',
        jpeg: 'image/jpeg',
        png: 'image/png',
        mp4: 'video/mp4',
    };

    const acceptedTypes = extensionsFormat.reduce((acc: { [key: string]: any }, ext: string) => {
        const mimeType = extensionToMime[ext.replace('.', '')];
        if (mimeType) {
            acc[mimeType] = [];
        }
        return acc;
    }, {});

    const maxSizeBytes = (props.maxFileSizeMB ?? 100) * 1024 * 1024;
    const [dropError, setDropError] = useState<string | null>(null);

    const { getRootProps, getInputProps } = useDropzone({
        accept: acceptedTypes,
        maxSize: maxSizeBytes,
        onDrop: (acceptedFiles) => {
            setDropError(null);
            if (acceptedFiles.length > 0) {
                const fileWithPreview = Object.assign(acceptedFiles[0], {
                    preview: URL.createObjectURL(acceptedFiles[0]),
                });
                onHandleDrop(fileWithPreview); // Pass a single file
            }
        },
        onDropRejected: (fileRejections) => {
            if (!fileRejections || fileRejections.length === 0) return;
            const first = fileRejections[0];
            const sizeErr = first.errors.find((e) => e.code === 'file-too-large');
            if (sizeErr) {
                setDropError(`File is too large. Max ${(props.maxFileSizeMB ?? 100)}MB`);
                return;
            }
            const msgs = first.errors.map((e) => e.message).join(', ');
            setDropError(msgs || 'File rejected');
        },
    });

    const handleRemoveClick = (event: React.MouseEvent<HTMLDivElement>) => {
        event.stopPropagation();
        onRemoveFile();
    };

    return (
        <>
            <label className="form-label">{label ?? displayLabel}</label>
            {description && <div className="text-muted small mb-2">{parse(description)}</div>}
            <div className={`dropzone ${error ? 'is-invalid' : file ? 'is-valid' : ''}`} {...getRootProps()}>
                <div role="presentation" tabIndex={0} className="file-upload-container">
                    <input accept="" type="file" tabIndex={-1} className="d-none" name={name} {...getInputProps()} />
                    {file ? (
                        <div className="d-flex align-items-center">
                            <span className="fw-semibold small">{file.name}</span>
                            <div className="d-flex align-items-center ms-3 text-danger file-delete-box" onClick={handleRemoveClick}>
                                <FontAwesomeIcon icon={['fal', 'trash']} />
                                <span className="ms-1 small">{t('delete')}</span>
                            </div>
                        </div>
                    ) : (
                        <div className="file-wrapper d-flex align-items-center justify-content-center">
                            <div className="file-upload-icon d-flex align-items-center justify-content-center border border-primary rounded-pill me-2" style={{ width: 50, height: 50 }}>
                                <FontAwesomeIcon icon={['fal', 'file-arrow-up']} size="xl" />
                            </div>
                            <span className="text-primary" style={{ fontSize: 20 }}>
                                <span>{placeholder ? placeholder : t('drop_or_upload_file')}</span>
                                <p className="mb-0 text-body opacity-50" style={{ fontSize: 12 }}>
                                    {t('approved_files')}
                                    {extensionsFormat.map((ext: string, index: number) => (
                                        <span key={ext}>
                                            {ext.replace('.', '')}
                                            {index < extensionsFormat.length - 1 && ', '}
                                        </span>
                                    ))}
                                    <br />
                                    {t('max_file_size')}: {props.maxFileSizeMB ?? 100}MB
                                </p>
                            </span>
                        </div>
                    )}
                </div>
            </div>
            {(dropError || error) && <div className="invalid-feedback">{parse(dropError ?? error ?? '')}</div>}
        </>
    );
};

export default FileUploadSingle;
